/*
 * @Author: linguang linguang@nextmar.com
 * @Date: 2025-04-14 12:50:46
 * @LastEditors: linguang linguang@nextmar.com
 * @LastEditTime: 2025-04-14 12:52:55
 * @FilePath: /zeelool-nextjs/src/components/Toast/index.tsx
 * @Description: 这是默认设置,请设置`customMade`, 打开koroFileHeader查看配置 进行设置: https://github.com/OBKoro1/koro1FileHeader/wiki/%E9%85%8D%E7%BD%AE
 */
'use client';

import { Box, keyframes } from '@mui/material';
import { ErrorTipOutlined, InfoOutlined, WapRightOutlined } from '@zeelool/icon';
import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'normal';

export interface ToastProps {
  /**
   * Title of the toast (optional)
   */
  title?: string;

  /**
   * Message content to display
   */
  content: string;

  /**
   * Whether the message is visible
   */
  open: boolean;

  /**
   * Type of toast
   */
  type?: ToastType;

  /**
   * Auto hide duration in milliseconds (default: 3000ms)
   */
  duration?: number;

  /**
   * Callback when message is closed
   */
  onClose?: () => void;
}

export interface ToastConfig {
  title?: string;
  content: string;
  duration?: number;
  onClose?: () => void;
}

// Define the animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
`;

// Icon mapping for different toast types
const IconMapping: Record<ToastType, React.ReactNode | null> = {
  success: <WapRightOutlined />,
  error: <ErrorTipOutlined />,
  warning: <InfoOutlined />,
  info: null,
  normal: null,
};

/**
 * Toast component for displaying notifications
 */
const Toast: React.FC<ToastProps> = ({
  title,
  content,
  open,
  type = 'normal',
  duration = 3000,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const icon = IconMapping[type];

  useEffect(() => {
    if (open) {
      setIsVisible(true);
      setIsLeaving(false);

      // Auto-hide after duration
      const timer = setTimeout(() => {
        setIsLeaving(true);

        // Allow animation to complete before fully removing
        setTimeout(() => {
          setIsVisible(false);
          onClose?.();
        }, 200);
      }, duration);

      return () => clearTimeout(timer);
    } else {
      setIsLeaving(true);
      setTimeout(() => {
        setIsVisible(false);
      }, 200);
    }
  }, [open, duration, onClose]);

  if (!isVisible) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          borderRadius: '8px',
          width: 'fit-content',
          minWidth: '320px',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          animation: isLeaving ? `${fadeOut} 0.2s ease-out forwards` : `${fadeIn} 0.2s ease-out`,
          fontFamily: 'BricolageGrotesque-Regular',
          overflow: 'hidden',
          padding: '14px 16px',
        }}
        className="max-w-[300px] lg:max-w-[600px]"
      >
        {/* With title layout */}
        {title && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
              }}
            >
              {icon && (
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'white',
                    fontSize: '20px',
                    flexShrink: 0,
                    marginRight: '12px',
                  }}
                >
                  {icon}
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box
                  component="span"
                  sx={{
                    marginBottom: '12px',
                    fontFamily: 'var(--font-bricolage-bold)',
                  }}
                  className="text-[14px] lg:text-[16px]"
                >
                  {title}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontFamily: 'var(--font-bricolage)',
                  }}
                  className="text-[13px] leading-[20px] lg:text-[14px] lg:leading-[1.5]"
                >
                  {content}
                </Box>
              </Box>
            </Box>
          </>
        )}

        {/* No title layout - icon and content on same line */}
        {!title && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {icon && (
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'white',
                  fontSize: '20px',
                  flexShrink: 0,
                }}
              >
                {icon}
              </Box>
            )}
            <Box
              component="span"
              sx={{
                fontSize: '14px',
                fontFamily: 'var(--font-bricolage)',
              }}
            >
              {content}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Toast container to keep track of all toasts
interface ToastInstance {
  key: string;
  config: ToastProps;
}

// Toast Function API
let toastContainer: HTMLDivElement | null = null;
let root: ReturnType<typeof createRoot> | null = null;
let toastInstances: ToastInstance[] = [];
let keyCounter = 0;

// Initialize container
const getContainer = () => {
  if (!toastContainer) {
    if (typeof document !== 'undefined') {
      toastContainer = document.createElement('div');
      toastContainer.className = 'toast-container';
      document.body.appendChild(toastContainer);
      root = createRoot(toastContainer);
    }
  }
  return { container: toastContainer, root };
};

// Render toasts
const renderToasts = () => {
  const { root: rt } = getContainer();

  if (rt && toastInstances.length) {
    rt.render(
      <>
        {toastInstances.map(({ key, config }) => (
          <Toast
            key={key}
            {...config}
            onClose={() => {
              // Remove toast from list when it closes
              toastInstances = toastInstances.filter((instance) => instance.key !== key);
              renderToasts();
              config.onClose?.();
            }}
          />
        ))}
      </>,
    );
  } else if (rt && toastInstances.length === 0) {
    rt.render(<></>);
  }
};

// Function to create a toast
const open = (config: ToastConfig & { type: ToastType }) => {
  getContainer();
  const key = `toast-${keyCounter++}`;

  const toastConfig: ToastProps = {
    ...config,
    open: true,
  };

  toastInstances.push({ key, config: toastConfig });
  renderToasts();

  return key;
};

// Function to close a toast
const close = (key: string) => {
  const index = toastInstances.findIndex((instance) => instance.key === key);
  if (index >= 0) {
    const instance = toastInstances[index];
    instance.config.open = false;
    renderToasts();
  }
};

// Toast API
const toast = {
  success: (config: string | ToastConfig) => {
    if (typeof config === 'string') {
      return open({ content: config, type: 'success' });
    }
    return open({ ...config, type: 'success' });
  },
  error: (config: string | ToastConfig) => {
    if (typeof config === 'string') {
      return open({ content: config, type: 'error' });
    }
    return open({ ...config, type: 'error' });
  },
  warning: (config: string | ToastConfig) => {
    if (typeof config === 'string') {
      return open({ content: config, type: 'warning' });
    }
    return open({ ...config, type: 'warning' });
  },
  info: (config: string | ToastConfig) => {
    if (typeof config === 'string') {
      return open({ content: config, type: 'info' });
    }
    return open({ ...config, type: 'info' });
  },
  normal: (config: string | ToastConfig) => {
    if (typeof config === 'string') {
      return open({ content: config, type: 'normal' });
    }
    return open({ ...config, type: 'normal' });
  },
  close,
};

export { toast };
export default Toast;
